// Génère un PDF du planning hebdomadaire pour l'impression/partage WhatsApp/email.
// Basé sur jsPDF + jspdf-autotable. 100% côté client (aucun appel backend).
// Palette volontairement légère : lisible en noir & blanc.

import { format } from "date-fns";
import { fr } from "date-fns/locale";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import type { Shift } from "@/types/shift.type";
import type { UserType } from "@/types/user.type";

const COULEURS = {
  // Titre : texte foncé sur fond blanc (pas de bandeau bleu)
  titreBg: [255, 255, 255] as [number, number, number],
  titreTexte: [15, 23, 42] as [number, number, number],

  // En-tête tableau : fond très léger + texte foncé
  headerBg: [241, 245, 249] as [number, number, number],
  headerTexte: [15, 23, 42] as [number, number, number],

  // Weekend en-tête : légèrement plus foncé
  weekendHeaderBg: [226, 232, 240] as [number, number, number],

  // Cellule avec créneau : fond très léger bleu (quasi blanc en N&B)
  shiftBg: [239, 246, 255] as [number, number, number],
  shiftTexte: [15, 23, 42] as [number, number, number],

  // Weekend corps : très léger
  weekendBg: [248, 250, 252] as [number, number, number],

  // Total : fond très léger vert
  totalBg: [240, 253, 244] as [number, number, number],
  totalTexte: [15, 23, 42] as [number, number, number],

  // Lignes alternées
  ligneAlt: [248, 250, 252] as [number, number, number],

  // Pied de page & traits
  grisTexte: [148, 163, 184] as [number, number, number],
  noir: [15, 23, 42] as [number, number, number],
};

type ExportParams = {
  companyName: string;
  weekStart: Date;
  weekEnd: Date;
  weekDays: Date[];
  employees: UserType[];
  getCellShifts: (employeeId: string, day: Date) => Shift[];
  getTotalMinutes: (employeeId: string) => number;
  formatDuration: (minutes: number) => string;
};

function getShiftMinutes(shift: Shift): number {
  if (shift.status === "CANCELLED") return 0;

  const start = new Date(shift.startTime).getTime();
  const end = new Date(shift.endTime).getTime();
  const totalMinutes = Math.round((end - start) / 60000) - (shift.breakMinutes ?? 0);

  return Math.max(totalMinutes, 0);
}

function formatCellContent(
  shifts: Shift[],
  formatDuration: (minutes: number) => string
): string {
  if (shifts.length === 0) return "";

  return shifts
    .map((s) => {
      const start = format(new Date(s.startTime), "HH:mm");
      const end = format(new Date(s.endTime), "HH:mm");
      const duration = getShiftMinutes(s);
      const label = `${start}-${end} (${formatDuration(duration)})`;
      const note = s.note ? `\n${s.note}` : "";
      const cancelled = s.status === "CANCELLED" ? " (annulé)" : "";

      return `${label}${cancelled}${note}`;
    })
    .join("\n\n");
}

export function exportPlanningToPDF({
  companyName,
  weekStart,
  weekEnd,
  weekDays,
  employees,
  getCellShifts,
  getTotalMinutes,
  formatDuration,
}: ExportParams) {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();

  // ─── En-tête page (lisible en N&B : pas de rectangle bleu plein) ───────
  const periodLabel = `Planning du ${format(weekStart, "d MMMM", { locale: fr })} au ${format(weekEnd, "d MMMM yyyy", { locale: fr })}`;

  doc.setFontSize(15);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COULEURS.titreTexte);
  doc.text(companyName, 14, 11);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...COULEURS.grisTexte);
  const generatedAt = `Généré le ${format(new Date(), "d MMM yyyy 'à' HH:mm", { locale: fr })}`;
  doc.text(generatedAt, pageWidth - 14, 11, { align: "right" });

  doc.setFontSize(10);
  doc.setTextColor(...COULEURS.noir);
  doc.text(periodLabel, 14, 19);

  doc.setDrawColor(...COULEURS.grisTexte);
  doc.setLineWidth(0.5);
  doc.line(14, 23, pageWidth - 14, 23);
  doc.setDrawColor(0, 0, 0);
  doc.setTextColor(0, 0, 0);

  const headers = [
    "Employé",
    ...weekDays.map((d) => {
      const dayName = format(d, "EEE", { locale: fr });
      const dayNum = format(d, "d MMM", { locale: fr });
      return `${dayName}\n${dayNum}`;
    }),
    "Total",
  ];

  const rows = employees.map((emp) => {
    const name = `${emp.firstName ?? ""} ${emp.lastName ?? ""}`.trim();
    const cells = weekDays.map((day) =>
      formatCellContent(getCellShifts(emp.id!, day), formatDuration)
    );
    const total = formatDuration(getTotalMinutes(emp.id!));
    return [name, ...cells, total];
  });

  autoTable(doc, {
    startY: 26,
    head: [headers],
    body: rows,
    theme: "grid",
    styles: {
      fontSize: 8,
      cellPadding: 2,
      valign: "middle",
      halign: "center",
      overflow: "linebreak",
      textColor: COULEURS.noir,
      lineColor: COULEURS.noir,
      lineWidth: 0.2,
    },
    tableLineColor: COULEURS.noir,
    tableLineWidth: 0.2,
    bodyStyles: {
      minCellHeight: 20,
    },
    headStyles: {
      fillColor: COULEURS.headerBg,
      textColor: COULEURS.headerTexte,
      fontStyle: "bold",
      halign: "center",
    },
    columnStyles: {
      0: { cellWidth: 45, fontStyle: "bold", halign: "left", valign: "middle" },
      8: { cellWidth: 22, fontStyle: "bold", halign: "center" },
    },
    didParseCell: (data) => {
      if (data.section === "head") {
        if (data.column.index === 6 || data.column.index === 7) {
          data.cell.styles.fillColor = COULEURS.weekendHeaderBg;
          data.cell.styles.textColor = COULEURS.headerTexte;
        }
        return;
      }

      if (data.section !== "body") return;

      const { index } = data.column;
      const rowOdd = data.row.index % 2 === 1;
      const isWeekendCol = index === 6 || index === 7;
      const hasShift =
        index > 0 && index < 8 && Boolean(data.cell.raw);

      if (index === 8) {
        data.cell.styles.fillColor = COULEURS.totalBg;
        data.cell.styles.textColor = COULEURS.totalTexte;
        data.cell.styles.fontStyle = "bold";
        return;
      }

      if (hasShift) {
        data.cell.styles.fillColor = COULEURS.shiftBg;
        data.cell.styles.textColor = COULEURS.shiftTexte;
        return;
      }

      if (isWeekendCol) {
        data.cell.styles.fillColor = COULEURS.weekendBg;
        data.cell.styles.textColor = COULEURS.noir;
        return;
      }

      data.cell.styles.fillColor = rowOdd
        ? COULEURS.ligneAlt
        : COULEURS.titreBg;
      data.cell.styles.textColor = COULEURS.noir;
    },
  });

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...COULEURS.noir);
  doc.text(
    `${companyName} — Généré via Pointo`,
    14,
    doc.internal.pageSize.getHeight() - 8
  );
  doc.setTextColor(0, 0, 0);

  const safeCompany = companyName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  const filename = `planning-${safeCompany}-${format(weekStart, "yyyy-MM-dd")}.pdf`;

  doc.save(filename);
}
